import { useEffect } from 'react';

import * as Sentry from '@sentry/nextjs';
import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import {
  GetMarketplacePropertyDetailsDocument,
  type GetMarketplacePropertyDetailsQuery,
} from 'components/marketplace/property/details/__generated__/marketplace-property.zeus';
import { mapMarketplaceProperty } from 'components/marketplace/property/details/propertyDetails';
import { ClientError } from 'graphql-request';

const LISTING_PREVIEW_CACHE_TIME_MS = 30_000;

const isMarketplacePropertyDetailsQuery = (
  data: unknown,
): data is GetMarketplacePropertyDetailsQuery => {
  if (typeof data !== 'object' || data === null || !('marketplace' in data)) {
    return false;
  }
  const marketplace = data.marketplace;
  return typeof marketplace === 'object' && marketplace !== null && 'listingDetails' in marketplace;
};

const getPartialMarketplacePropertyDetails = (
  error: unknown,
): GetMarketplacePropertyDetailsQuery | null => {
  const data = error instanceof ClientError ? error.response.data : undefined;
  return isMarketplacePropertyDetailsQuery(data) ? data : null;
};

const fetchMarketplaceListingPreview = async (addressId: string, signal: AbortSignal) => {
  try {
    const data = await requestZeusFromBrowser(
      GetMarketplacePropertyDetailsDocument,
      { addressId, includeSimilarListings: false },
      signal,
    );
    return mapMarketplaceProperty(data);
  } catch (error) {
    const partialData = getPartialMarketplacePropertyDetails(error);
    if (partialData) {
      const property = mapMarketplaceProperty(partialData);
      if (property) {
        Sentry.captureException(error, {
          tags: { feature: 'marketplace-browse', operation: 'listingPreview' },
        });
        return property;
      }
    }
    throw error;
  }
};

export const useMarketplaceListingPreview = (addressId: string | null) => {
  const query = useQuery({
    queryKey: ['marketplace', 'listing-preview', addressId],
    queryFn: addressId
      ? ({ signal }) => fetchMarketplaceListingPreview(addressId, signal)
      : skipToken,
    placeholderData: addressId ? keepPreviousData : undefined,
    staleTime: LISTING_PREVIEW_CACHE_TIME_MS,
    gcTime: LISTING_PREVIEW_CACHE_TIME_MS,
  });

  useEffect(() => {
    if (query.error) {
      Sentry.captureException(query.error, {
        tags: { feature: 'marketplace-browse', operation: 'listingPreview' },
      });
    }
  }, [query.error]);

  return query;
};
